import { ThunkAction } from 'redux-thunk';
import { RootState } from 'src/_common/models/redux/store';
import { CHAR_LIMITS } from 'config/constants';
import { openToastr, openAlertDialog } from '@Common/handlers/services';
import logger from 'core/logger/logger';
import { getReduxStore } from 'src/_common/redux/store';
import FirestoreIns from 'src/authentication/handlers/firestore';
import cyrilicData from 'public/jsonData/cyrilic_lookup.json';
import { getWordCount, getWordIndexes, inputWordLimitCheck } from '@Common/handlers/helper';
import {
  SystemStartedSummarizationAction,
  SystemFinishedSummarizationAction,
  SystemErrorSummarizationAction,
  UserChangedInputTextAction,
} from '../models/actions';
import { USER_CHANGED_INPUT_TEXT } from '../models/actions/names';

import {
  systemStartedSummarizationAction,
  systemFinishedSummarizationAction,
  systemErrorSummarizationAction,
} from './systemActions';
import {
  getNumberOfSentences,
  getSummarySentenceSequence,
  getOutputKey,
  addParagraphBreaks,
} from '../handlers/logic';
import { getSummary } from '../handlers/api';
import { transformExtResponse } from '../handlers/transformation';
import { OutputAbs } from '../models/store';

export const changeInputTextAction = (text: string): UserChangedInputTextAction => {
  let inputPara = text.trim();
  for (let i = 0; i < inputPara.length; i += 1) {
    if (cyrilicData) {
      const a: string = inputPara[i];
      if ((<any>cyrilicData)[a]) {
        inputPara = inputPara.replace(inputPara[i], (<any>cyrilicData)[a]);
      }
    }
  }

  return {
    type: USER_CHANGED_INPUT_TEXT,
    payload: {
      inputPara,
      numberOfChars: inputPara.length,
      numberOfWords: getWordCount(inputPara),
    },
  };
};

const containsAll = (array1: any[], array2: any[]) =>
  array2.every((array2Item) => array1.includes(array2Item));

const sameMembers = (array1: any[], array2: any[]) => {
  if (!array1 || !array2) return false;
  return containsAll(array1, array2) && containsAll(array2, array1);
};

export type SummarizeActions =
  | SystemStartedSummarizationAction
  | SystemFinishedSummarizationAction
  | SystemErrorSummarizationAction
  | UserChangedInputTextAction;

interface SumPara {
  summaryMode: string;
  absLengthGauge: number;
  extLengthGauge: number;
  isOutputLoaded: boolean;
}

const getSumarizerParameters = (isRequillWithChangedConfig?: boolean): SumPara => {
  const store = getReduxStore()?.getState() as RootState;
  const cs = store.smzr;
  const summaryMode = cs.controls.summaryMode;
  const absLengthGauge = cs.controls.absLengthGauge;
  const extLengthGauge = cs.controls.extLengthGauge;

  let isOutputLoaded = false;

  if (isRequillWithChangedConfig) {
    if (summaryMode === 'abs') {
      // co implies current_output
      const co = cs.output[`abs~${absLengthGauge}`];
      if (co && Object.keys(co).length !== 0) isOutputLoaded = true;
    } else if (summaryMode === 'ext') {
      const co = cs.output.ext;
      if (co && Object.keys(co.sentences).length !== 0) isOutputLoaded = true;
    }
  }

  return {
    summaryMode,
    absLengthGauge,
    extLengthGauge,
    isOutputLoaded,
  };
};

export const setOutputAction =
  (
    inputPara: string,
    summaryMode: string,
    absLengthGauge: number,
    extLengthGauge: number,
    isRequillWithChangedConfig: boolean
  ): ThunkAction<void, RootState, null, SummarizeActions> =>
  (dispatch) => {
    if (inputPara && inputPara !== '') {
      dispatch(
        systemStartedSummarizationAction(
          inputPara,
          summaryMode as 'ext' | 'abs',
          isRequillWithChangedConfig,
          getNumberOfSentences(inputPara)
        )
      );
      getSummary(
        {
          para: inputPara,
          type: summaryMode,
          ratio: absLengthGauge,
        },
        summaryMode as 'ext' | 'abs'
      )
        .then((res) => {
          if (summaryMode === 'ext') {
            const extOutput = transformExtResponse(res);
            extOutput.summarySentenceSequence = getSummarySentenceSequence(
              extOutput,
              extLengthGauge
            );
            dispatch(systemFinishedSummarizationAction({ ...extOutput, summaryMode }));
          } else if (summaryMode === 'abs') {
            if (res.summary === '') res.summary = 'Please enter enough text to summarize.';
            dispatch(
              systemFinishedSummarizationAction({
                summaryPara: res.summary,
                id: res.id,
                ratio: res.ratio,
                lengthGauge: absLengthGauge,
                numberOfSentences: getNumberOfSentences(res.summary),
                summaryMode: 'abs',
                modelId: res.modelId,
              })
            );
          } else {
            logger('SUMMARIZATION FAILED', [
              "Something is wrong. Shouldn't come here. Value of summary mode is: ",
              summaryMode,
            ]);
          }
        })
        .catch((err) => {
          logger('QUILL_ACTION_SUMMARIZATION_ERR', err);
          dispatch(systemErrorSummarizationAction(err));
        });
    }
  };

export const summarizeAction =
  (triggeredBy?: string, forced?: boolean): ThunkAction<void, RootState, null, SummarizeActions> =>
  (dispatch, getState) => {
    const store = getReduxStore()?.getState() as RootState;
    const output = store.smzr.output;
    if (store.common.maintenance.isMaintenance) {
      openAlertDialog(
        true,
        'QuillBot Under Maintenance',
        'QuillBot is currently down for maintenance. Check back soon.'
      );
      return;
    }

    const cs = getState().smzr;
    const summaryMode = cs.controls.summaryMode;
    const absLengthGauge = cs.controls.absLengthGauge;
    const extLengthGauge = cs.controls.extLengthGauge;
    const keywords = cs.controls.selectedKeywords;
    const Firestore = FirestoreIns.getInstance();

    let inputPara = cs.input.inputPara;

    let currentWordLimit: number = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
    if (Firestore.isPremiumUser()) {
      currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
    } else if (Firestore.authenticated) {
      currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
    }
    const wordIndexes = getWordIndexes(inputPara);
    if (getWordCount(wordIndexes) > currentWordLimit) {
      const splitIndex = inputWordLimitCheck(wordIndexes, currentWordLimit);
      inputPara = inputPara.substr(0, splitIndex);
      const inputElem = document.getElementById('inputBoxSummarizer');
      if (inputElem) {
        inputElem.innerHTML = inputPara;
      }
      dispatch(changeInputTextAction(inputPara));
      openToastr(`Word limit of ${currentWordLimit} exceeded, extra words trimmed.`);
    }

    const origText = cs.output.origText;
    let isOutputLoaded = false;

    if (cs.controls.isQuilling) {
      openToastr('Already processing');
      return;
    }

    if (!inputPara.length) {
      if (triggeredBy) {
        openToastr('Please enter some text to Summarize');
      }
      return;
    }
    const outputKey = getOutputKey(summaryMode, absLengthGauge);
    if (
      inputPara === output.origText &&
      output[outputKey] &&
      (output.ext.numberOfSentences || (output[outputKey] as OutputAbs).numberOfSentences) &&
      (summaryMode === 'abs' ||
        output.ext.numberOfSentences === extLengthGauge ||
        extLengthGauge > output.origNumberOfSentences) &&
      summaryMode === 'abs' &&
      sameMembers(output.keywords as string[], keywords)
      // ((!output.keywords && !keywords.length) || sameMembers(output.keywords as string[], keywords))
    ) {
      openToastr('Text is already summarized');
      return;
    }
    let isRequillWithChangedConfig = false;
    if (inputPara === origText) {
      isRequillWithChangedConfig = true;
      if (summaryMode === 'abs') {
        // co implies current_output
        const co = cs.output[`abs~${absLengthGauge}`];
        if (co && Object.keys(co).length !== 0) isOutputLoaded = true;
      } else if (summaryMode === 'ext') {
        const co = cs.output.ext;
        if (co && Object.keys(co.sentences).length !== 0) isOutputLoaded = true;
      }
    }

    if (
      summaryMode === 'abs' &&
      (output[outputKey] as OutputAbs) &&
      !sameMembers(output.keywords as string[], keywords)
    ) {
      isOutputLoaded = false;
    }

    if (forced || !isOutputLoaded) {
      if (inputPara && inputPara !== '') {
        dispatch(
          systemStartedSummarizationAction(
            inputPara,
            summaryMode as 'ext' | 'abs',
            isRequillWithChangedConfig,
            getNumberOfSentences(inputPara),
            triggeredBy
          )
        );
        getSummary(
          {
            para: inputPara,
            type: summaryMode,
            ratio: absLengthGauge,
            ...(summaryMode === 'abs' && keywords.length ? { keywords } : null),
          },
          summaryMode
        )
          .then((res) => {
            if (summaryMode === 'ext') {
              const extOutput = transformExtResponse(res);
              extOutput.summarySentenceSequence = getSummarySentenceSequence(
                extOutput,
                extLengthGauge
              );
              dispatch(systemFinishedSummarizationAction({ ...extOutput, summaryMode }));
            } else if (summaryMode === 'abs') {
              if (res.summary === '') res.summary = 'Please enter enough text to summarize.';
              res.summary = addParagraphBreaks(res.summary);
              dispatch(
                systemFinishedSummarizationAction({
                  summaryPara: res.summary,
                  id: res.id,
                  ratio: res.ratio,
                  lengthGauge: absLengthGauge,
                  numberOfSentences: getNumberOfSentences(res.summary),
                  summaryMode: 'abs',
                  modelId: res.modelId,
                  keywords: res?.keywords || [],
                  outputKeywords: res?.output_keywords || [],
                })
              );
            } else {
              logger('SUMMARIZATION FAILED', [
                "Something is wrong. Shouldn't come here. Value of summary mode is: ",
                summaryMode,
              ]);
            }
          })
          .catch((err) => {
            logger('QUILL_ACTION_SUMMARIZATION_ERR', err);
            dispatch(systemErrorSummarizationAction(err));
          });
      }
    }
  };

export const summarizeActionForSelectedText =
  (
    text: string,
    context?: string,
    forced?: boolean
  ): ThunkAction<void, RootState, null, SummarizeActions> =>
  (dispatch, getState) => {
    const store = getState() as RootState;
    const cs = store.smzr;
    const output = store.smzr.output;
    const origText = output.origText;
    const keywords = cs.controls.selectedKeywords;

    if (store.common.maintenance.isMaintenance) {
      openAlertDialog(
        true,
        'QuillBot Under Maintenance',
        'QuillBot is currently down for maintenance. Check back soon.'
      );
      return;
    }
    const isRequillWithChangedConfig = text === origText;

    const { summaryMode, absLengthGauge, extLengthGauge, isOutputLoaded } = getSumarizerParameters(
      isRequillWithChangedConfig
    );
    const Firestore = FirestoreIns.getInstance();

    let inputPara = text;
    let currentWordLimit: number = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
    if (Firestore.isPremiumUser()) {
      currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
    } else if (Firestore.authenticated) {
      currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
    }

    const wordIndexes = getWordIndexes(inputPara);
    if (getWordCount(wordIndexes) > currentWordLimit) {
      const splitIndex = inputWordLimitCheck(wordIndexes, currentWordLimit);
      inputPara = inputPara.substr(0, splitIndex);
      openToastr(`Word limit of ${currentWordLimit} exceeded, extra words trimmed.`);
    }

    if (cs.controls.isQuilling) {
      openToastr('Already processing');
      return;
    }

    const outputKey = getOutputKey(summaryMode, absLengthGauge);
    if (
      inputPara === output.origText &&
      output[outputKey] &&
      (output.ext.numberOfSentences || (output[outputKey] as OutputAbs).numberOfSentences) &&
      (summaryMode === 'abs' ||
        output.ext.numberOfSentences === extLengthGauge ||
        extLengthGauge > output.origNumberOfSentences) &&
      summaryMode === 'abs' &&
      sameMembers(output.keywords as string[], keywords)
    ) {
      openToastr('Text is already summarized');
      return;
    }

    if (forced || !isOutputLoaded) {
      if (inputPara && inputPara !== '') {
        dispatch(
          setOutputAction(
            inputPara,
            summaryMode,
            absLengthGauge,
            extLengthGauge,
            isRequillWithChangedConfig
          )
        );
      }
    }
  };
